import { useState, useEffect, useCallback } from 'react';
import useAuth from './useAuth';
import userService from '../services/user';
import socketService from '../services/socket';

/*
 * [2026-10-28] 알림 때문에 하는 '다시 읽기'는 한 화면에 떠 있는 금액 칸 전체가 잔액 조회 한 번을 나눠 쓴다(모듈 단위 예약).
 *   WHY: 이 훅은 공용 상태가 아니라 금액 칸(상단바·입금·출금·코인입금·출석·게임·베팅슬립)마다 따로 상태를 가진다.
 *     칸마다 따로 다시 읽으면 알림 하나에 잔액 조회가 칸 수만큼 나간다. 그런데 잔액 조회(유저 API routes/userApi.js /balance)는
 *     아너 전송 회원이 게임 중이 아니면 DB 를 읽기 전에 아너 회원 조회로 "카지노머니"를 먼저 맞추고, 그 조회는 공용 대기줄
 *     (services/globalHonorRateLimiter.js — 회원당 1초·전체 0.5초)을 탄다. 같은 회원 조회가 같은 순간 둘이면 뒤의 것이 회원당 제한에 걸려
 *     대기줄 맨 앞에서 기다리며 다른 회원 조회까지 막는다(독립 검토 지적). 롤링 적립은 베팅 1건마다 같은 회원에게 알림이 두 번 온다
 *     (관리 API 직접 + 유저 API 중계 routes/rollingNotification.js). 그래서 ① 칸들이 조회 한 번을 나눠 쓰고 ② 1초 안에 온 알림은 한 번으로 묶는다.
 *   '포인트'만 바뀌는 알림으로 읽은 결과는 "포인트" 칸에만 쓴다 — 지갑 칸은 실시간 잔액 알림이 더 새 값을 줄 수 있다
 *     (스핀 중 늦게 도착한 조회 응답이 "카지노머니"를 한 스핀 전 값으로 되돌리지 않게, 독립 검토 지적).
 */
const REFETCH_DELAY_MS = 1000;
const sharedRefetch = {
  subscribers: new Set(),                 // 금액 칸마다의 적용 함수 (data, scope) => void — 로그인한 동안만 등록
  timer: null,
  scope: { wallet: false, point: false }, // 이번 묶음에서 맞출 칸 — wallet: 카지노머니·보유금·포인트 전부 / point: 포인트만
  converting: 0,                          // 이 화면에서 '전환'을 요청 중인 칸 수 — 그동안 온 알림은 전환이 끝난 뒤 한 번 읽는다
  deferred: false,
};

function scheduleSharedRefetch(scope) {
  if (scope.wallet) sharedRefetch.scope.wallet = true;
  if (scope.point) sharedRefetch.scope.point = true;
  if (sharedRefetch.timer) return; // 이미 예약됨 — 1초 안에 온 알림은 한 번으로 묶는다
  sharedRefetch.timer = setTimeout(runSharedRefetch, REFETCH_DELAY_MS);
}

async function runSharedRefetch() {
  sharedRefetch.timer = null;
  // 전환 요청 중이면 미룬다 — 전환이 끝나면(convertPoints finally) 모아 둔 칸 범위 그대로 한 번 읽는다
  if (sharedRefetch.converting > 0) { sharedRefetch.deferred = true; return; }
  const scope = sharedRefetch.scope;
  sharedRefetch.scope = { wallet: false, point: false };
  // 떠 있는 칸이 없으면(로그아웃 등) 읽지 않는다 — 쓸 곳 없는 조회를 보내지 않는다
  if (sharedRefetch.subscribers.size === 0) return;
  try {
    const data = await userService.getBalance();
    sharedRefetch.subscribers.forEach((apply) => apply(data, scope));
  } catch (error) {
    console.error('잔액 다시 읽기 실패:', error);
  }
}

const useBalance = () => {
  const [balance, setBalance] = useState(0);
  const [points, setPoints] = useState(0);
  const [sportsMoney, setSportsMoney] = useState(0); // 스포츠 머니
  const [apiMode, setApiMode] = useState(null); // 활성 지갑 모드 (sports면 gameMoney가 지갑)
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // 잔액 정보 가져오기
  const fetchBalance = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const data = await userService.getBalance();
      setBalance(data.balance);
      setPoints(data.point);
      setSportsMoney(data.gameMoney || 0); // 스포츠 머니 설정
      setApiMode(data.apiMode || null); // 활성 지갑 모드 설정
    } catch (error) {
      console.error('잔액 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // 잔액 새로고침
  const refreshBalance = async () => {
    await fetchBalance();
  };

  // 포인트 새로고침 (잔액과 함께 조회됨)
  const refreshPoints = async () => {
    await fetchBalance();
  };

  // [2026-10-28] 이 칸을 모듈 단위 다시 읽기(맨 위 sharedRefetch)에 등록한다 — 로그인한 동안만.
  useEffect(() => {
    if (!user) return undefined;
    const apply = (data, scope) => {
      if (!data) return;
      if (scope.wallet) {
        setBalance(data.balance);
        setSportsMoney(data.gameMoney || 0);
        setApiMode(data.apiMode || null);
      }
      if (scope.wallet || scope.point) setPoints(data.point);
    };
    sharedRefetch.subscribers.add(apply);
    return () => { sharedRefetch.subscribers.delete(apply); };
  }, [user]);

  // 웹소켓 잔액 업데이트 리스너
  useEffect(() => {
    // 소켓 이벤트 핸들러
    //
    // [2026-10-21] 아래 종류의 알림은 알림 값을 쓰지 않고 다시 읽는다(REFETCH_TYPES — 서버 알림의 type 계약 값).
    //   · 'rolling_convert'(포인트 전환 — 유저 API userApi.js) · 'deposit'(입금 승인) · 'withdrawal'·'withdrawal_refund'(출금 승인·반려 — 관리 API)
    //   입금·출금 알림의 포인트 값은 '회원 포인트 칸'뿐이라(롤링금 빠짐) 화면 "포인트"(롤링금 + 회원 포인트)와 다르다 —
    //   블랙 2026-05 분기처럼 그 값을 그대로 넣으면 롤링금이 있는 회원의 "포인트"가 적게 보였다(독립 검토 지적). 다시 읽으면 충전 보너스도 바로 보인다.
    const REFETCH_TYPES = ['rolling_convert', 'deposit', 'withdrawal', 'withdrawal_refund'];
    // [2026-10-28] 관리자 '롤링전환'(관리 API routes/rollingTransfer.js) 알림은 type 없이 action 'rolling_transfer' 만 싣는다.
    //   그 balance 도 '더한 쪽 지갑의 새 값'(스포츠 지갑 회원이면 보유금)이라 그대로 넣으면 "카지노머니" 칸에 보유금 금액이 보이고,
    //   롤링금 차감은 싣지 않아 "포인트"가 줄지 않았다 → 아래 'rolling_convert' 와 같은 이유로 다시 읽는다(socket.js 가 action 을 넘긴다).
    const REFETCH_ACTIONS = ['rolling_transfer'];
    const needsRefetch = (d) => REFETCH_TYPES.includes(d?.type) || REFETCH_ACTIONS.includes(d?.action);
    // [2026-10-21] 포인트 전환 알림(type 'rolling_convert')은 알림 값을 쓰지 않고 다시 읽는다.
    //   WHY: 서버 포인트 전환은 회원이 스포츠 지갑 모드면 보유금(스포츠)에, 아니면 카지노머니에 더하는데
    //   알림의 balance 는 "더한 쪽 지갑의 새 값"뿐이고 어느 지갑인지 싣지 않는다. 그대로 넣으면 스포츠 모드에서
    //   상단 "카지노머니" 칸에 보유금 금액이 보인다. 그렇다고 무시만 하면 전환을 누르지 않은 다른 화면
    //   (상단바 ↔ 입금·출금 화면)이 새로고침 전까지 옛 금액으로 남는다(블랙과 같게 무시만 했던 1차 수정의 결함,
    //   독립 검토 지적). 그래서 떠 있는 모든 칸의 두 지갑·포인트를 다시 읽는다([2026-10-28] 칸들이 조회 한 번을 나눠 씀 — 맨 위 설명).
    //   그 밖의 종류(정산·지급 등)는 종전대로 알림 값 사용.
    const handleBalanceUpdate = (data) => {
      console.log('💰 잔액 업데이트:', data);
      if (needsRefetch(data)) { scheduleSharedRefetch({ wallet: true }); return; } // 위 WHY 참조
      setBalance(data.balance);
      setPoints(data.point || points); // point가 없으면 기존값 유지
      // 스포츠 머니 실시간 업데이트
      if (data.gameMoney !== undefined) {
        setSportsMoney(data.gameMoney);
      }
    };

    // 롤링금 업데이트 핸들러 (Socket.IO 직접 처리)
    //   [2026-10-28] 알림 값을 "포인트"에 넣지 않고 다시 읽는다 — 아래 handleWindowPointsUpdate 의 WHY 참조.
    const handleRollingUpdate = (data) => {
      console.log('🎯 [useBalance] 롤링금 업데이트 수신:', data);
      scheduleSharedRefetch({ point: true });
    };

    // window 이벤트 핸들러 (socket.js에서 발생시키는 CustomEvent)
    //   [2026-10-21] socket.js 가 서버 'balance:changed' 를 type 을 실어 이 이벤트로 다시 보내므로(포인트 전환 알림은
    //   실제로 이 길로 온다) 위 소켓 핸들러와 같은 이유로 알림 값 대신 다시 읽는다.
    const handleWindowBalanceUpdate = (event) => {
      console.log('💰 잔액 업데이트 (window event):', event.detail);
      if (needsRefetch(event.detail)) { scheduleSharedRefetch({ wallet: true }); return; } // 위 WHY 참조
      if (event.detail && event.detail.balance !== undefined) {
        setBalance(event.detail.balance);
      }
      // 스포츠 머니 실시간 업데이트
      if (event.detail && event.detail.gameMoney !== undefined) {
        setSportsMoney(event.detail.gameMoney);
      }
      // [2026-10-21] 미니게임 창을 닫을 때 화면이 직접 잔액을 다시 읽어 보내는 알림(type 'minigame_sync')의 포인트는
      //   잔액 조회 결과 그대로(롤링금 + 회원 포인트)라 화면 "포인트"와 같은 값이다 → 그대로 넣는다(블랙·루미나·base 미니게임 화면이 보냄,
      //   디자인1·디자인2 는 포인트를 싣지 않아 해당 없음). 입금·출금 알림의 포인트(회원 포인트 칸뿐)는 위 REFETCH_TYPES 로 다시 읽는다.
      if (event.detail?.type === 'minigame_sync' && event.detail.point !== undefined) {
        setPoints(event.detail.point);
      }
    };

    // 포인트 업데이트 이벤트 핸들러 (socket.js 가 롤링금·포인트 알림과 게임 중 잔액 동기화 알림의 rollingAmount 를 이 이벤트로 다시 보낸다)
    //   [2026-10-28] 값을 그대로 "포인트"에 넣지 않고 다시 읽는다(결과는 "포인트" 칸에만 — 맨 위 설명).
    //   WHY: 화면 "포인트" = 롤링금(슬롯+카지노) + 회원 포인트(유저 API userApi.js 잔액 조회)인데, 알림마다 실어 보내는 값의 뜻이 다르다.
    //     · 게임 중 5초 동기화(아너 전송·레드폭스 — honorSyncService·redfoxRealtimeBalanceService)는 쓰지 않는 옛 통합 칸(거의 다 0)을 실어
    //       게임하는 동안 "포인트"가 5초마다 그 값으로 덮였다.
    //     · 롤링 적립 알림(utils/rollingCalculator.js)은 슬롯+카지노만, 스포츠 롤링 알림(utils/sportsRollingCalculator.js)은 스포츠까지 더한 값,
    //       중계 알림(유저 API routes/rollingNotification.js)은 베팅 1건분 롤링금을 싣는다 — 회원 포인트(충전 보너스 등)가 빠지거나 엉뚱한 값이 보였다.
    //     · 관리자 롤링 조정 알림(관리 API memberAdjustment.js)은 스포츠 롤링까지 더한 합계를 싣는다.
    //   그래서 알림은 "바뀌었다"는 신호로만 쓰고 값은 한 곳(잔액 조회)에서 읽는다 — 보내는 쪽을 하나씩 맞추면 새 알림이 생길 때 또 어긋난다.
    const handleWindowPointsUpdate = (event) => {
      console.log('🎯 포인트 업데이트 (window event):', event.detail);
      scheduleSharedRefetch({ point: true });
    };

    // 소켓 이벤트 등록
    //   (이 직접 구독은 이 칸이 붙을 때 소켓이 이미 있어야 걸린다 — 새로고침·새 탭으로 앱이 뜰 때 먼저 붙는 상단바처럼 소켓이 아직 없으면
    //    socket.js 가 다시 보내는 window 이벤트로만 받는다)
    socketService.onBalanceUpdate(handleBalanceUpdate);
    socketService.on('rolling:update', handleRollingUpdate);
    socketService.on('rolling:updated', handleRollingUpdate); // 하위 호환성

    // window 이벤트 등록
    window.addEventListener('balanceUpdated', handleWindowBalanceUpdate);
    window.addEventListener('pointsUpdated', handleWindowPointsUpdate);

    return () => {
      socketService.off('balanceUpdate', handleBalanceUpdate);
      socketService.off('rolling:update', handleRollingUpdate);
      socketService.off('rolling:updated', handleRollingUpdate);
      window.removeEventListener('balanceUpdated', handleWindowBalanceUpdate);
      window.removeEventListener('pointsUpdated', handleWindowPointsUpdate);
    };
  }, []);

  // 컴포넌트 마운트 시 잔액 정보 로드
  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  // 포인트 전환
  //   [2026-10-21] 응답의 balance 는 "포인트를 더한 쪽 지갑의 새 값"이라 회원이 스포츠 지갑 모드면 보유금(스포츠)
  //   금액이다. 이것을 카지노머니 상태에 넣으면 상단 "카지노머니" 칸이 틀리고 보유금 칸은 옛 값으로 남는다.
  //   그래서 이 칸은 응답 직후 두 지갑·포인트를 다시 읽는다. 같이 떠 있는 다른 칸은 서버의 전환 알림으로 다시 읽는다(위 이벤트 핸들러).
  //   [2026-10-28] 전환 요청 중에 차례가 온 알림 다시 읽기는 미뤘다가(sharedRefetch.converting), 전환이 끝나면 성공·실패 모두 한 번 읽는다.
  //   WHY: 실패하면 이 칸은 다시 읽지 않아, 그사이 온 다른 알림(입금 승인 등)을 놓친 채 옛 값으로 남았다(독립 검토 지적).
  const convertPoints = async () => {
    if (points <= 0) {
      throw new Error('전환할 포인트가 없습니다.');
    }

    sharedRefetch.converting += 1;
    setLoading(true);
    try {
      const data = await userService.convertPoints(points);

      if (data.success) {
        // DB 기준 두 지갑(카지노머니·보유금)과 포인트를 한 번에 다시 읽는다 — 위 WHY 참조
        await fetchBalance();
        return { success: true, message: data.message };
      }

      throw new Error(data.error || '포인트 전환에 실패했습니다.');
    } catch (error) {
      console.error('포인트 전환 실패:', error);
      throw error;
    } finally {
      sharedRefetch.converting -= 1;
      if (sharedRefetch.converting === 0 && sharedRefetch.deferred) {
        sharedRefetch.deferred = false;
        scheduleSharedRefetch({}); // 미뤄 둔 칸 범위(sharedRefetch.scope) 그대로 1초 뒤 한 번
      }
      setLoading(false);
    }
  };

  return {
    balance,
    points,
    sportsMoney,
    apiMode,
    loading,
    refreshBalance,
    refreshPoints,
    convertPoints
  };
};

export default useBalance;
